import { TEAMS_LINK, TeamsLogo, WHATSAPP_NUMBER, WhatsAppLogo } from "@/lib";
import { motion } from "framer-motion";
import { Calculator } from "lucide-react";
import { useState } from "react";

export default function CostEstimator() {
    const ON_DEMAND_RATE = 70;
    const RETAINER_RATE = 50;

    const RETAINER_PACKAGES = [
        { hours: 10, price: 10 * RETAINER_RATE },
        { hours: 20, price: 20 * RETAINER_RATE },
        { hours: 40, price: 40 * RETAINER_RATE },
    ];

    const [planType, setPlanType] = useState('on-demand');
    const [onDemandHours, setOnDemandHours] = useState(5);
    const [retainerPackage, setRetainerPackage] = useState(RETAINER_PACKAGES[0]);

    const totalCost = planType === 'on-demand'
        ? onDemandHours * ON_DEMAND_RATE
        : retainerPackage.price;

    const summary = planType === 'on-demand'
        ? { plan: 'On-Demand (Starter)', hours: onDemandHours, cost: totalCost }
        : { plan: 'Business Retainer', hours: retainerPackage.hours, cost: totalCost };

    const formatCurrency = (amount: number): string => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
    };

    const whatsappMessage = encodeURIComponent(`I used the Cost Estimator and am interested in the ${summary.plan} package, estimated at ${formatCurrency(summary.cost)} for ${summary.hours} hours. Let's talk!`);

    return (
        <section id="calculator" className="py-20 sm:py-24 bg-gray-900 border-t border-gray-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center">
                    <span className="text-sm font-semibold uppercase text-indigo-400 tracking-widest">
                        Cost Estimator
                    </span>
                    <h2 className="mt-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
                        Calculate Your Consultancy Investment
                    </h2>
                    <p className="mt-4 text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
                        Get an instant estimate for flexible on-demand support or strategic monthly retainer packages.
                    </p>
                </div>

                <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-12 bg-gray-800 p-8 md:p-12 rounded-2xl shadow-2xl border border-gray-700">
                    {/* --- Calculator Controls (Left Panel) --- */}
                    <div className="flex flex-col space-y-10">
                        
                        {/* 1. Plan Type Selector */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, amount: 0.5 }}
                            transition={{ duration: 0.5 }}
                        >
                            <label className="text-lg font-bold text-white mb-3 block">1. Select Engagement Model</label>
                            <div className="flex flex-col sm:flex-row gap-4">
                                {/* On-Demand Button */}
                                <button
                                    onClick={() => setPlanType('on-demand')}
                                    className={`flex-1 p-5 rounded-xl text-left transition-all duration-300 transform border-2 ${planType === 'on-demand' 
                                        ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-600/30 border-indigo-500' 
                                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600 border-gray-700'}`}
                                >
                                    <span className="font-extrabold block text-xl">On-Demand</span>
                                    <span className="text-sm opacity-90 mt-1">Pay as you go for urgent, hourly support.</span>
                                </button>
                                {/* Retainer Button */}
                                <button
                                    onClick={() => setPlanType('retainer')}
                                    className={`flex-1 p-5 rounded-xl text-left transition-all duration-300 transform border-2 ${planType === 'retainer' 
                                        ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-600/30 border-indigo-500' 
                                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600 border-gray-700'}`}
                                >
                                    <span className="font-extrabold block text-xl">Monthly Retainer</span>
                                    <span className="text-sm opacity-90 mt-1">Pre-paid credit for predictable, continuous support.</span>
                                </button>
                            </div>
                        </motion.div>

                        {/* 2. Hours Selector */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, amount: 0.5 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                        >
                            <label className="text-lg font-bold text-white mb-3 block">2. Specify Scope</label>
                            
                            {/* On-Demand Slider */}
                            {planType === 'on-demand' && (
                                <div className="bg-gray-700 p-6 rounded-xl border border-gray-600 shadow-inner">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-gray-300">Estimated Hours Needed:</span>
                                        <span className="text-3xl font-extrabold text-indigo-400">{onDemandHours}</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="1"
                                        max="20"
                                        step="1"
                                        value={onDemandHours}
                                        onChange={(e) => setOnDemandHours(Number(e.target.value))}
                                        className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                                    />
                                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                                        <span>1 hour (Minimum)</span>
                                        <span>20 hours (Max Scope)</span>
                                    </div>
                                </div>
                            )}
                            
                            {/* Retainer Packages */}
                            {planType === 'retainer' && (
                                <div className="flex flex-col gap-4">
                                    {RETAINER_PACKAGES.map((pkg) => (
                                        <button
                                            key={pkg.hours}
                                            onClick={() => setRetainerPackage(pkg)}
                                            className={`p-4 rounded-xl text-left transition-all duration-300 transform border-2 ${retainerPackage.hours === pkg.hours 
                                                ? 'bg-indigo-700 text-white shadow-lg border-indigo-500' 
                                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600 border-gray-700'}`}
                                        >
                                            <span className="font-extrabold text-xl">{pkg.hours} Hours / Month</span>
                                            <span className="text-sm opacity-90 block">Savings included: {formatCurrency(pkg.hours * ON_DEMAND_RATE - pkg.price)} vs. On-Demand Rate</span>
                                            <span className="text-sm opacity-90 block">Rate: {formatCurrency(RETAINER_RATE)}/hr vs. {formatCurrency(ON_DEMAND_RATE)}/hr</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    </div>

                    {/* --- Calculator Summary (Right Panel) --- */}
                    <motion.div 
                        className="bg-gray-700 p-8 rounded-2xl flex flex-col justify-center items-center text-center border-4 border-indigo-500 shadow-2xl shadow-indigo-500/30"
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, amount: 0.5 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        <Calculator className="w-16 h-16 text-indigo-400 mb-6" />
                        <h3 className="text-3xl font-extrabold text-white mb-4">Your Estimated Investment</h3>
                        <p className="text-xl text-gray-300 mb-6">Plan Selected: <span className="font-extrabold text-indigo-300">{summary.plan}</span></p>

                        <div className="bg-gray-800 p-6 rounded-xl w-full mb-6">
                            <div className="flex justify-between items-center text-lg mb-3 border-b border-gray-700 pb-3">
                                <span className="text-gray-400 font-medium">Credit Hours:</span>
                                <span className="font-extrabold text-white">{summary.hours}</span>
                            </div>
                            <div className="flex justify-between items-center text-lg">
                                <span className="text-gray-400 font-medium">Hourly Rate:</span>
                                <span className="font-extrabold text-white">
                                    {formatCurrency(planType === 'on-demand' ? ON_DEMAND_RATE : RETAINER_RATE)}
                                </span>
                            </div>
                        </div>

                        <div className="w-full border-t border-gray-700 pt-6">
                            <span className="text-lg text-gray-400 block">Total Investment</span>
                            <span className="text-6xl font-extrabold text-indigo-400 block my-2">
                                {formatCurrency(totalCost)}
                            </span>
                            <span className="text-xl font-medium text-gray-300 block">
                                {planType === 'on-demand' ? 'One-Time Estimate' : 'Per Month (Discounted)'}
                            </span>
                        </div>

                        {/* --- CTA Buttons --- */}
                        <motion.a
                            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-10 w-full block text-center bg-green-500 text-gray-900 px-6 py-4 rounded-xl text-lg font-bold shadow-xl flex items-center justify-center transition-all duration-300"
                            whileHover={{ scale: 1.05, backgroundColor: '#10B981' }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <WhatsAppLogo className="w-5 h-5 mr-3" />
                            Discuss Estimate on WhatsApp
                        </motion.a>
                        <motion.a
                            href={TEAMS_LINK}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-4 w-full block text-center bg-indigo-700 text-white px-6 py-3 rounded-xl text-lg font-semibold shadow-md flex items-center justify-center transition-all duration-300"
                            whileHover={{ scale: 1.05, backgroundColor: '#4338CA' }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <TeamsLogo className="w-5 h-5 mr-3" />
                            Join Teams Community
                        </motion.a>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}